import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { money } from '../lib/money';
import { useConfirm } from './ConfirmDialog';
import './TicketExpenses.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const day = (v) => (v ? String(v).slice(0, 10) : '—');

// What the server enforces, named here so the two cannot disagree about what
// will be accepted. routes/attachments.js: 10MB, one file.
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_MB = MAX_BYTES / 1024 / 1024;

/**
 * What a contractor is out of pocket for on a ticket.
 *
 * WHY THIS IS A COMPONENT RATHER THAN MARKUP ON A PAGE. It was built in
 * October (migration 021) and lived in exactly one place: the contractor's own
 * ticket page at /my-tickets/:id, reachable only with a portal token. Gordon
 * went looking for it and could not find it - twice, because the portal's own
 * card does not navigate to that page: pressing a ticket there expands the
 * card in place, deliberately, since /tickets/:id is a staff route that used
 * to throw a contractor out to the login screen. So the feature sat on the one
 * page the portal never visits.
 *
 * It now appears in four places, so it is one component. Four copies of a
 * claim form is how two of them come to disagree about what a receipt is
 * optional for.
 *
 * `canClaim` is the caller's decision, not this component's: the ticket page
 * knows about its own lock, the work order page knows whether the viewer is
 * the contractor who holds it, and the staff ticket view knows an approver is
 * not the person out of pocket. Read-only is a real mode here rather than a
 * disabled form - an approver seeing what has been claimed is the point.
 *
 * THE RECEIPT. A claim used to be able to point only at a file ALREADY on the
 * ticket, which meant attaching the photo somewhere else first and coming
 * back - and on the portal card it meant the panel had to live behind "Show
 * Details", because that is when the file list loads, while logging an hour
 * sat in plain view above it. A contractor can now attach the receipt in the
 * same action as the claim: pick the photo or the PDF and it is uploaded to
 * the ticket and pointed at in one go. The old picker is still here behind a
 * toggle, for a receipt that is already on the ticket, and its list is fetched
 * only when somebody asks for it - so the common path needs no data at all,
 * which is what lets this panel sit beside the hours where it belongs.
 */
const TicketExpenses = ({
  ticketId,
  auth,
  canClaim = false,
  attachments = [],
  onChanged
}) => {
  const [rows, setRows] = useState([]);
  const [amount, setAmount] = useState('');
  const [what, setWhat] = useState('');
  const [spentOn, setSpentOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [receipt, setReceipt] = useState('');
  const [picking, setPicking] = useState(false);
  const [onTicket, setOnTicket] = useState(attachments);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirm, confirmDialog] = useConfirm();
  const fileRef = useRef(null);

  // Whatever the parent already knows about wins; it is usually fresher.
  useEffect(() => {
    if (attachments.length > 0) setOnTicket(attachments);
  }, [attachments]);

  const load = useCallback(async () => {
    if (!ticketId) return;
    try {
      const res = await axios.get(`${API_BASE}/tickets/${ticketId}/expenses`, auth);
      setRows(Array.isArray(res.data) ? res.data : (res.data?.expenses || []));
      setError(null);
    } catch (err) {
      // A read that fails is not the same as nothing claimed, and saying
      // "nothing claimed against this job" when the read broke is how an
      // approver signs off an invoice short.
      setError(err.response?.data?.error || 'Could not read what has been claimed');
    }
  }, [ticketId, auth]);

  useEffect(() => { load(); }, [load]);

  /**
   * The files already on the ticket, fetched only when somebody asks to pick
   * one. On the portal card this panel is rendered for every assignment, so
   * fetching this on mount would be one request per card for a list most
   * contractors will never open - and loading it lazily is what lets the panel
   * sit beside the hours instead of behind "Show Details".
   */
  const openPicker = async () => {
    setPicking(true);
    if (onTicket.length > 0) return;
    try {
      const res = await axios.get(`${API_BASE}/tickets/${ticketId}/attachments`, auth);
      setOnTicket(Array.isArray(res.data) ? res.data : []);
    } catch {
      // Not worth an error message: uploading is the main path and still
      // works. An empty list says so for itself.
      setOnTicket([]);
    }
  };

  const act = async (work) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through');
    } finally {
      setBusy(false);
    }
  };

  const chooseFile = (e) => {
    const picked = e.target.files?.[0] || null;
    setError(null);
    if (!picked) { setFile(null); return; }
    // Checked before the upload rather than after, because a 12MB photo over a
    // phone connection is a long wait to be told no.
    if (picked.size > MAX_BYTES) {
      setError(`That file is ${(picked.size / 1024 / 1024).toFixed(1)}MB and the `
        + `limit is ${MAX_MB}MB. A photo taken at a smaller size will do.`);
      e.target.value = '';
      setFile(null);
      return;
    }
    setFile(picked);
    // A chosen file and a chosen existing attachment are the same field, so
    // picking one clears the other rather than leaving both set.
    setReceipt('');
  };

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const claim = (e) => {
    e.preventDefault();
    if (!Number(amount) || !what.trim()) return;
    return act(async () => {
      // The receipt goes up FIRST and its id goes on the claim. The other way
      // round would leave a claim with no receipt behind it if the upload then
      // failed - and "no receipt" is exactly the thing the approver is being
      // asked to notice, so it must never be an accident.
      let receiptId = receipt || null;
      if (file) {
        const body = new FormData();
        body.append('file', file);
        const up = await axios.post(
          `${API_BASE}/tickets/${ticketId}/attachments`, body,
          {
            ...auth,
            headers: {
              ...(auth?.headers || {}),
              // Left to the browser, which adds the multipart boundary.
              // Setting this by hand omits the boundary and the server cannot
              // parse the body at all.
              'Content-Type': 'multipart/form-data'
            }
          });
        receiptId = up.data?.id || null;
      }

      await axios.post(`${API_BASE}/tickets/${ticketId}/expenses`, {
        amount: Number(amount),
        description: what.trim(),
        spent_on: spentOn,
        receipt_attachment_id: receiptId
      }, auth);
      setAmount('');
      setWhat('');
      setReceipt('');
      setPicking(false);
      clearFile();
    });
  };

  const withdraw = async (x) => {
    if (!(await confirm({ title: `Withdraw ${x.description || 'this expense'} (${money(x.amount)})?`,
      message: 'It comes off the claim on this ticket. You can enter it again.', confirmLabel: 'Withdraw', danger: true }))) return;
    act(() => axios.delete(`${API_BASE}/expenses/${x.id}`, auth));
  };

  const total = rows.reduce((a, b) => a + Number(b.amount || 0), 0);

  return (
    <div className="tex">
      {/* Approved by the same decision as the hours, so there is no submit
          button of its own - resolving the ticket sends both. A receipt is
          optional: the approver is shown which lines have none and decides
          from there, rather than a form blocking somebody who lost a till
          slip. */}
      {canClaim && (
        <form className="tex-form" onSubmit={claim}>
          <label>
            <span>Date</span>
            <input type="date" value={spentOn}
                   onChange={(e) => setSpentOn(e.target.value)} />
          </label>
          <label>
            <span>Amount (€)</span>
            <input type="text" inputMode="decimal" value={amount} placeholder="24.50"
                   onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} />
          </label>
          <label className="tex-grow">
            <span>What you bought</span>
            <input type="text" value={what}
                   onChange={(e) => setWhat(e.target.value)} />
          </label>

          <div className="tex-receipt-field">
            <span className="tex-label">Receipt</span>
            {file ? (
              <span className="tex-chosen">
                <span className="tex-chosen-name">{file.name}</span>
                <button type="button" className="tex-clear" onClick={clearFile}
                        aria-label={`Remove ${file.name}`}>✕</button>
              </span>
            ) : (
              <span className="tex-receipt-controls">
                {/* `accept` is a hint to the picker, not a rule: on a phone
                    image/* offers the camera and the camera roll, which is how
                    a receipt actually gets captured. A PDF is the other half.
                    The server takes anything; only the size is enforced. */}
                <input
                  ref={fileRef}
                  type="file"
                  className="tex-file"
                  accept="image/*,application/pdf,.pdf,.heic,.heif"
                  onChange={chooseFile}
                />
                {!picking ? (
                  <button type="button" className="tex-link" onClick={openPicker}>
                    or one already on this ticket
                  </button>
                ) : (
                  <select value={receipt} className="tex-pick"
                          onChange={(e) => setReceipt(e.target.value)}>
                    <option value="">
                      {onTicket.length > 0 ? 'None' : 'Nothing on this ticket yet'}
                    </option>
                    {onTicket.map((a) => (
                      <option key={a.id} value={a.id}>{a.file_name}</option>
                    ))}
                  </select>
                )}
              </span>
            )}
          </div>

          <button type="submit" disabled={busy || !Number(amount) || !what.trim()}>
            {busy && file ? 'Uploading…' : 'Claim it'}
          </button>
        </form>
      )}

      {rows.length === 0 ? (
        <p className="tex-quiet">
          {error ? 'Could not read the claims on this job.' : 'Nothing claimed against this job.'}
        </p>
      ) : (
        <>
          <div className="tex-list">
            {rows.map((x) => (
              <div className="tex-row" key={x.id}>
                <span className="tex-when">{day(x.spent_on)}</span>
                <span className="tex-what">
                  {x.description}
                  {/* WHO ENTERED IT. Two kinds of person can now record a
                      claim - the contractor who was out of pocket, and a
                      member of staff handed the receipt - and an approver
                      agreeing a figure should not have to guess which. Named
                      only when it was NOT the contractor, because on a
                      contractor's own claim it is the obvious case and would
                      be noise on every line. */}
                  {x.claimed_by && x.claimed_by_contractor === false && (
                    <span className="tex-by"> · entered by {x.claimed_by}</span>
                  )}
                  {x.receipt_file_name ? (
                    <span className="tex-receipt"> · {x.receipt_file_name}</span>
                  ) : (
                    // Said on the line rather than left blank: an approver
                    // deciding whether to agree to a figure needs to know
                    // there is nothing behind it.
                    <span className="tex-noreceipt"> · no receipt</span>
                  )}
                </span>
                <span className="tex-amount">{money(x.amount)}</span>
                {canClaim && (
                  <button type="button" className="tex-withdraw" disabled={busy}
                          onClick={() => withdraw(x)}
                          aria-label={`Withdraw ${x.description}`}>
                    Withdraw
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="tex-total">
            <span>Total claimed</span>
            <span>{money(total)}</span>
          </div>
        </>
      )}

      {error && <p className="tex-error">{error}</p>}
      {confirmDialog}
    </div>
  );
};

export default TicketExpenses;
